import * as PIXI from "pixi.js";
import {config} from "../config";
import {PredefinedPIXIAnchorPoint} from "../PredefinedPIXIAnchorPoint";
import data from "./configs/data";
import {GamePanel} from "./userInterface/GamePanel";
import {VideoPokerCardCombinationName} from "./VideoPokerCardCombinationName";
import {VideoPokerCombinationMultiplier} from "./VideoPokerCombinationMultiplier";
import {WinningDotAnimation} from "./WinningDotAnimation";

export class PayTable extends PIXI.Container {

    private readonly WINNING_DOT_INITIAL_POSITION = new PIXI.Point(-411, 0);

    private readonly JACKS_OR_BETTER_Y_POSITION = 110;
    private readonly TWO_PAIRS_Y_POSITION = 87;
    private readonly THREE_OF_A_KIND_Y_POSITION = 64;
    private readonly STRAIGHT_Y_POSITION = 41;
    private readonly FLUSH_Y_POSITION = 18;
    private readonly FULL_HOUSE_Y_POSITION = -5;
    private readonly FOUR_OF_A_KIND_Y_POSITION = -28;
    private readonly STRAIGHT_FLUSH_Y_POSITION = -51;
    private readonly ROYAL_FLUSH_Y_POSITION = -74;

    private readonly TEXT_STYLE = {
        fontSize: 20,
        fontFamily: "RobotoMedium",
        fill: "#fff",
    };

    private readonly ROYAL_FLUSH_MULTIPLIER_POSITION = new PIXI.Point(-92, -75);
    private readonly STRAIGHT_FLUSH_MULTIPLIER_POSITION = new PIXI.Point(-92, -51);
    private readonly FOUR_OF_A_KIND_MULTIPLIER_POSITION = new PIXI.Point(-92, -28);
    private readonly FULL_HOUSE_MULTIPLIER_POSITION = new PIXI.Point(-92, -5);
    private readonly FLUSH_MULTIPLIER_POSITION = new PIXI.Point(-92, 18);
    private readonly STRAIGHT_MULTIPLIER_POSITION = new PIXI.Point(-92, 41);
    private readonly THREE_OF_A_KIND_MULTIPLIER_POSITION = new PIXI.Point(-92, 64);
    private readonly TWO_PAIRS_MULTIPLIER_POSITION = new PIXI.Point(-92, 87);
    private readonly JACKS_OR_BETTER_MULTIPLIER_POSITION = new PIXI.Point(-92, 110);

    private readonly ROYAL_FLUSH_BET_POSITION = new PIXI.Point(5, -75);
    private readonly STRAIGHT_FLUSH_BET_POSITION = new PIXI.Point(5, -51);
    private readonly FOUR_OF_A_KIND_BET_POSITION = new PIXI.Point(5, -28);
    private readonly FULL_HOUSE_BET_POSITION = new PIXI.Point(5, -5);
    private readonly FLUSH_BET_POSITION = new PIXI.Point(5, 18);
    private readonly STRAIGHT_BET_POSITION = new PIXI.Point(5, 41);
    private readonly THREE_OF_A_KIND_BET_POSITION = new PIXI.Point(5, 64);
    private readonly TWO_PAIRS_BET_POSITION = new PIXI.Point(5, 87);
    private readonly JACKS_OR_BETTER_BET_POSITION = new PIXI.Point(5, 110);

    private readonly ROYAL_FLUSH_WIN_POSITION = new PIXI.Point(148, -75);
    private readonly STRAIGHT_FLUSH_WIN_POSITION = new PIXI.Point(148, -51);
    private readonly FOUR_OF_A_KIND_WIN_POSITION = new PIXI.Point(148, -28);
    private readonly FULL_HOUSE_WIN_POSITION = new PIXI.Point(148, -5);
    private readonly FLUSH_WIN_POSITION = new PIXI.Point(148, 18);
    private readonly STRAIGHT_WIN_POSITION = new PIXI.Point(148, 41);
    private readonly THREE_OF_A_KIND_WIN_POSITION = new PIXI.Point(148, 64);
    private readonly TWO_PAIRS_WIN_POSITION = new PIXI.Point(148, 87);
    private readonly JACKS_OR_BETTER_WIN_POSITION = new PIXI.Point(148, 110);

    private readonly ROYAL_FLUSH_MAX_WIN_POSITION = new PIXI.Point(338, -75);
    private readonly STRAIGHT_FLUSH_MAX_WIN_POSITION = new PIXI.Point(338, -51);
    private readonly FOUR_OF_A_KIND_MAX_WIN_POSITION = new PIXI.Point(338, -28);
    private readonly FULL_HOUSE_MAX_WIN_POSITION = new PIXI.Point(338, -5);
    private readonly FLUSH_MAX_WIN_POSITION = new PIXI.Point(338, 18);
    private readonly STRAIGHT_MAX_WIN_POSITION = new PIXI.Point(338, 41);
    private readonly THREE_OF_A_KIND_MAX_WIN_POSITION = new PIXI.Point(338, 64);
    private readonly TWO_PAIRS_MAX_WIN_POSITION = new PIXI.Point(338, 87);
    private readonly JACKS_OR_BETTER_MAX_WIN_POSITION = new PIXI.Point(338, 110);

    private royalFlushBetValueText: PIXI.Text;
    private straightFlushBetValueText: PIXI.Text;
    private fourOfAKindBetValueText: PIXI.Text;
    private fullHouseBetValueText: PIXI.Text;
    private flushBetValueText: PIXI.Text;
    private straightBetValueText: PIXI.Text;
    private threeOfAKindBetValueText: PIXI.Text;
    private twoPairsFlushBetValueText: PIXI.Text;
    private jacksOrBetterFlushBetValueText: PIXI.Text;

    private royalFlushWinValueText: PIXI.Text;
    private straightFlushWinValueText: PIXI.Text;
    private fourOfAKindWinValueText: PIXI.Text;
    private fullHouseWinValueText: PIXI.Text;
    private flushWinValueText: PIXI.Text;
    private straightWinValueText: PIXI.Text;
    private threeOfAKindWinValueText: PIXI.Text;
    private twoPairsFlushWinValueText: PIXI.Text;
    private jacksOrBetterFlushWinValueText: PIXI.Text;

    private royalFlushMaxWinValueText: PIXI.Text;
    private straightFlushMaxWinValueText: PIXI.Text;
    private fourOfAKindMaxWinValueText: PIXI.Text;
    private fullHouseMaxWinValueText: PIXI.Text;
    private flushMaxWinValueText: PIXI.Text;
    private straightMaxWinValueText: PIXI.Text;
    private threeOfAKindMaxWinValueText: PIXI.Text;
    private twoPairsFlushMaxWinValueText: PIXI.Text;
    private jacksOrBetterFlushMaxWinValueText: PIXI.Text;

    private winningDotAnimation: WinningDotAnimation;

    constructor() {
        super();
        this.createAndAddBackground();
        this.createAndAddWinningDotAnimation();
        this.createAndAddMultiplierTexts();
        this.createAndAddBetTexts();
        this.createAndAddWinTexts();
        this.createAndAddMaxWinTexts();
        GamePanel.getInstance().addOnBetValueChangedListener(this.onBetChanged.bind(this));
        this.onBetChanged(GamePanel.getInstance().getBetValue());
    }

    public showDot(videoPokerCardCombinationName: VideoPokerCardCombinationName): void {
        this.winningDotAnimation.visible = true;
        switch (videoPokerCardCombinationName) {
            case VideoPokerCardCombinationName.JACKS_OR_BETTER:
                this.winningDotAnimation.position.y = this.JACKS_OR_BETTER_Y_POSITION;
                break;
            case VideoPokerCardCombinationName.TWO_PAIRS:
                this.winningDotAnimation.position.y = this.TWO_PAIRS_Y_POSITION;
                break;
            case VideoPokerCardCombinationName.THREE_OF_A_KIND:
                this.winningDotAnimation.position.y = this.THREE_OF_A_KIND_Y_POSITION;
                break;
            case VideoPokerCardCombinationName.STRAIGHT:
                this.winningDotAnimation.position.y = this.STRAIGHT_Y_POSITION;
                break;
            case VideoPokerCardCombinationName.FLUSH:
                this.winningDotAnimation.position.y = this.FLUSH_Y_POSITION;
                break;
            case VideoPokerCardCombinationName.FULL_HOUSE:
                this.winningDotAnimation.position.y = this.FULL_HOUSE_Y_POSITION;
                break;
            case VideoPokerCardCombinationName.FOUR_OF_A_KIND:
                this.winningDotAnimation.position.y = this.FOUR_OF_A_KIND_Y_POSITION;
                break;
            case VideoPokerCardCombinationName.STRAIGHT_FLUSH:
                this.winningDotAnimation.position.y = this.STRAIGHT_FLUSH_Y_POSITION;
                break;
            case VideoPokerCardCombinationName.ROYAL_FLUSH:
                this.winningDotAnimation.position.y = this.ROYAL_FLUSH_Y_POSITION;
                break;
        }
    }

    public hideDot(): void {
        this.winningDotAnimation.visible = false;
    }

    private createAndAddMultiplierTexts(): void {
        let multiplier = new PIXI.Text(
            "x" + VideoPokerCombinationMultiplier.get(VideoPokerCardCombinationName.ROYAL_FLUSH), this.TEXT_STYLE);
        multiplier.position = this.ROYAL_FLUSH_MULTIPLIER_POSITION;
        multiplier.anchor = PredefinedPIXIAnchorPoint.CENTER;
        this.addChild(multiplier);
        multiplier = new PIXI.Text(
            "x" + VideoPokerCombinationMultiplier.get(VideoPokerCardCombinationName.STRAIGHT_FLUSH), this.TEXT_STYLE);
        multiplier.position = this.STRAIGHT_FLUSH_MULTIPLIER_POSITION;
        multiplier.anchor = PredefinedPIXIAnchorPoint.CENTER;
        this.addChild(multiplier);
        multiplier = new PIXI.Text(
            "x" + VideoPokerCombinationMultiplier.get(VideoPokerCardCombinationName.FOUR_OF_A_KIND), this.TEXT_STYLE);
        multiplier.position = this.FOUR_OF_A_KIND_MULTIPLIER_POSITION;
        multiplier.anchor = PredefinedPIXIAnchorPoint.CENTER;
        this.addChild(multiplier);
        multiplier = new PIXI.Text(
            "x" + VideoPokerCombinationMultiplier.get(VideoPokerCardCombinationName.FULL_HOUSE), this.TEXT_STYLE);
        multiplier.position = this.FULL_HOUSE_MULTIPLIER_POSITION;
        multiplier.anchor = PredefinedPIXIAnchorPoint.CENTER;
        this.addChild(multiplier);
        multiplier = new PIXI.Text(
            "x" + VideoPokerCombinationMultiplier.get(VideoPokerCardCombinationName.FLUSH), this.TEXT_STYLE);
        multiplier.position = this.FLUSH_MULTIPLIER_POSITION;
        multiplier.anchor = PredefinedPIXIAnchorPoint.CENTER;
        this.addChild(multiplier);
        multiplier = new PIXI.Text(
            "x" + VideoPokerCombinationMultiplier.get(VideoPokerCardCombinationName.STRAIGHT), this.TEXT_STYLE);
        multiplier.position = this.STRAIGHT_MULTIPLIER_POSITION;
        multiplier.anchor = PredefinedPIXIAnchorPoint.CENTER;
        this.addChild(multiplier);
        multiplier = new PIXI.Text(
            "x" + VideoPokerCombinationMultiplier.get(VideoPokerCardCombinationName.THREE_OF_A_KIND), this.TEXT_STYLE);
        multiplier.position = this.THREE_OF_A_KIND_MULTIPLIER_POSITION;
        multiplier.anchor = PredefinedPIXIAnchorPoint.CENTER;
        this.addChild(multiplier);
        multiplier = new PIXI.Text(
            "x" + VideoPokerCombinationMultiplier.get(VideoPokerCardCombinationName.TWO_PAIRS), this.TEXT_STYLE);
        multiplier.position = this.TWO_PAIRS_MULTIPLIER_POSITION;
        multiplier.anchor = PredefinedPIXIAnchorPoint.CENTER;
        this.addChild(multiplier);
        multiplier = new PIXI.Text(
            "x" + VideoPokerCombinationMultiplier.get(VideoPokerCardCombinationName.JACKS_OR_BETTER), this.TEXT_STYLE);
        multiplier.position = this.JACKS_OR_BETTER_MULTIPLIER_POSITION;
        multiplier.anchor = PredefinedPIXIAnchorPoint.CENTER;
        this.addChild(multiplier);
    }

    private createAndAddBetTexts(): void {
        this.royalFlushBetValueText = new PIXI.Text("", this.TEXT_STYLE);
        this.royalFlushBetValueText.position = this.ROYAL_FLUSH_BET_POSITION;
        this.royalFlushBetValueText.anchor = PredefinedPIXIAnchorPoint.CENTER;
        this.addChild(this.royalFlushBetValueText);
        this.straightFlushBetValueText = new PIXI.Text("", this.TEXT_STYLE);
        this.straightFlushBetValueText.position = this.STRAIGHT_FLUSH_BET_POSITION;
        this.straightFlushBetValueText.anchor = PredefinedPIXIAnchorPoint.CENTER;
        this.addChild(this.straightFlushBetValueText);
        this.fourOfAKindBetValueText = new PIXI.Text("", this.TEXT_STYLE);
        this.fourOfAKindBetValueText.position = this.FOUR_OF_A_KIND_BET_POSITION;
        this.fourOfAKindBetValueText.anchor = PredefinedPIXIAnchorPoint.CENTER;
        this.addChild(this.fourOfAKindBetValueText);
        this.fullHouseBetValueText = new PIXI.Text("", this.TEXT_STYLE);
        this.fullHouseBetValueText.position = this.FULL_HOUSE_BET_POSITION;
        this.fullHouseBetValueText.anchor = PredefinedPIXIAnchorPoint.CENTER;
        this.addChild(this.fullHouseBetValueText);
        this.flushBetValueText = new PIXI.Text("", this.TEXT_STYLE);
        this.flushBetValueText.position = this.FLUSH_BET_POSITION;
        this.flushBetValueText.anchor = PredefinedPIXIAnchorPoint.CENTER;
        this.addChild(this.flushBetValueText);
        this.straightBetValueText = new PIXI.Text("", this.TEXT_STYLE);
        this.straightBetValueText.position = this.STRAIGHT_BET_POSITION;
        this.straightBetValueText.anchor = PredefinedPIXIAnchorPoint.CENTER;
        this.addChild(this.straightBetValueText);
        this.threeOfAKindBetValueText = new PIXI.Text("", this.TEXT_STYLE);
        this.threeOfAKindBetValueText.position = this.THREE_OF_A_KIND_BET_POSITION;
        this.threeOfAKindBetValueText.anchor = PredefinedPIXIAnchorPoint.CENTER;
        this.addChild(this.threeOfAKindBetValueText);
        this.twoPairsFlushBetValueText = new PIXI.Text("", this.TEXT_STYLE);
        this.twoPairsFlushBetValueText.position = this.TWO_PAIRS_BET_POSITION;
        this.twoPairsFlushBetValueText.anchor = PredefinedPIXIAnchorPoint.CENTER;
        this.addChild(this.twoPairsFlushBetValueText);
        this.jacksOrBetterFlushBetValueText = new PIXI.Text("", this.TEXT_STYLE);
        this.jacksOrBetterFlushBetValueText.position = this.JACKS_OR_BETTER_BET_POSITION;
        this.jacksOrBetterFlushBetValueText.anchor = PredefinedPIXIAnchorPoint.CENTER;
        this.addChild(this.jacksOrBetterFlushBetValueText);
    }

    private createAndAddWinTexts(): void {
        this.royalFlushWinValueText = new PIXI.Text("", this.TEXT_STYLE);
        this.royalFlushWinValueText.position = this.ROYAL_FLUSH_WIN_POSITION;
        this.royalFlushWinValueText.anchor = PredefinedPIXIAnchorPoint.CENTER;
        this.addChild(this.royalFlushWinValueText);
        this.straightFlushWinValueText = new PIXI.Text("", this.TEXT_STYLE);
        this.straightFlushWinValueText.position = this.STRAIGHT_FLUSH_WIN_POSITION;
        this.straightFlushWinValueText.anchor = PredefinedPIXIAnchorPoint.CENTER;
        this.addChild(this.straightFlushWinValueText);
        this.fourOfAKindWinValueText = new PIXI.Text("", this.TEXT_STYLE);
        this.fourOfAKindWinValueText.position = this.FOUR_OF_A_KIND_WIN_POSITION;
        this.fourOfAKindWinValueText.anchor = PredefinedPIXIAnchorPoint.CENTER;
        this.addChild(this.fourOfAKindWinValueText);
        this.fullHouseWinValueText = new PIXI.Text("", this.TEXT_STYLE);
        this.fullHouseWinValueText.position = this.FULL_HOUSE_WIN_POSITION;
        this.fullHouseWinValueText.anchor = PredefinedPIXIAnchorPoint.CENTER;
        this.addChild(this.fullHouseWinValueText);
        this.flushWinValueText = new PIXI.Text("", this.TEXT_STYLE);
        this.flushWinValueText.position = this.FLUSH_WIN_POSITION;
        this.flushWinValueText.anchor = PredefinedPIXIAnchorPoint.CENTER;
        this.addChild(this.flushWinValueText);
        this.straightWinValueText = new PIXI.Text("", this.TEXT_STYLE);
        this.straightWinValueText.position = this.STRAIGHT_WIN_POSITION;
        this.straightWinValueText.anchor = PredefinedPIXIAnchorPoint.CENTER;
        this.addChild(this.straightWinValueText);
        this.threeOfAKindWinValueText = new PIXI.Text("", this.TEXT_STYLE);
        this.threeOfAKindWinValueText.position = this.THREE_OF_A_KIND_WIN_POSITION;
        this.threeOfAKindWinValueText.anchor = PredefinedPIXIAnchorPoint.CENTER;
        this.addChild(this.threeOfAKindWinValueText);
        this.twoPairsFlushWinValueText = new PIXI.Text("", this.TEXT_STYLE);
        this.twoPairsFlushWinValueText.position = this.TWO_PAIRS_WIN_POSITION;
        this.twoPairsFlushWinValueText.anchor = PredefinedPIXIAnchorPoint.CENTER;
        this.addChild(this.twoPairsFlushWinValueText);
        this.jacksOrBetterFlushWinValueText = new PIXI.Text("", this.TEXT_STYLE);
        this.jacksOrBetterFlushWinValueText.position = this.JACKS_OR_BETTER_WIN_POSITION;
        this.jacksOrBetterFlushWinValueText.anchor = PredefinedPIXIAnchorPoint.CENTER;
        this.addChild(this.jacksOrBetterFlushWinValueText);
    }

    private createAndAddMaxWinTexts(): void {
        this.royalFlushMaxWinValueText = new PIXI.Text("", this.TEXT_STYLE);
        this.royalFlushMaxWinValueText.position = this.ROYAL_FLUSH_MAX_WIN_POSITION;
        this.royalFlushMaxWinValueText.anchor = PredefinedPIXIAnchorPoint.CENTER;
        this.addChild(this.royalFlushMaxWinValueText);
        this.straightFlushMaxWinValueText = new PIXI.Text("", this.TEXT_STYLE);
        this.straightFlushMaxWinValueText.position = this.STRAIGHT_FLUSH_MAX_WIN_POSITION;
        this.straightFlushMaxWinValueText.anchor = PredefinedPIXIAnchorPoint.CENTER;
        this.addChild(this.straightFlushMaxWinValueText);
        this.fourOfAKindMaxWinValueText = new PIXI.Text("", this.TEXT_STYLE);
        this.fourOfAKindMaxWinValueText.position = this.FOUR_OF_A_KIND_MAX_WIN_POSITION;
        this.fourOfAKindMaxWinValueText.anchor = PredefinedPIXIAnchorPoint.CENTER;
        this.addChild(this.fourOfAKindMaxWinValueText);
        this.fullHouseMaxWinValueText = new PIXI.Text("", this.TEXT_STYLE);
        this.fullHouseMaxWinValueText.position = this.FULL_HOUSE_MAX_WIN_POSITION;
        this.fullHouseMaxWinValueText.anchor = PredefinedPIXIAnchorPoint.CENTER;
        this.addChild(this.fullHouseMaxWinValueText);
        this.flushMaxWinValueText = new PIXI.Text("", this.TEXT_STYLE);
        this.flushMaxWinValueText.position = this.FLUSH_MAX_WIN_POSITION;
        this.flushMaxWinValueText.anchor = PredefinedPIXIAnchorPoint.CENTER;
        this.addChild(this.flushMaxWinValueText);
        this.straightMaxWinValueText = new PIXI.Text("", this.TEXT_STYLE);
        this.straightMaxWinValueText.position = this.STRAIGHT_MAX_WIN_POSITION;
        this.straightMaxWinValueText.anchor = PredefinedPIXIAnchorPoint.CENTER;
        this.addChild(this.straightMaxWinValueText);
        this.threeOfAKindMaxWinValueText = new PIXI.Text("", this.TEXT_STYLE);
        this.threeOfAKindMaxWinValueText.position = this.THREE_OF_A_KIND_MAX_WIN_POSITION;
        this.threeOfAKindMaxWinValueText.anchor = PredefinedPIXIAnchorPoint.CENTER;
        this.addChild(this.threeOfAKindMaxWinValueText);
        this.twoPairsFlushMaxWinValueText = new PIXI.Text("", this.TEXT_STYLE);
        this.twoPairsFlushMaxWinValueText.position = this.TWO_PAIRS_MAX_WIN_POSITION;
        this.twoPairsFlushMaxWinValueText.anchor = PredefinedPIXIAnchorPoint.CENTER;
        this.addChild(this.twoPairsFlushMaxWinValueText);
        this.jacksOrBetterFlushMaxWinValueText = new PIXI.Text("", this.TEXT_STYLE);
        this.jacksOrBetterFlushMaxWinValueText.position = this.JACKS_OR_BETTER_MAX_WIN_POSITION;
        this.jacksOrBetterFlushMaxWinValueText.anchor = PredefinedPIXIAnchorPoint.CENTER;
        this.addChild(this.jacksOrBetterFlushMaxWinValueText);
        (window as any).jacksOrBetterFlushMaxWinValueText = this.jacksOrBetterFlushMaxWinValueText;
    }

    private onBetChanged(newBetValue: number): void {
        this.royalFlushBetValueText.text = newBetValue.toFixed(config.betDisplayPrecision);
        this.straightBetValueText.text = newBetValue.toFixed(config.betDisplayPrecision);
        this.fourOfAKindBetValueText.text = newBetValue.toFixed(config.betDisplayPrecision);
        this.fullHouseBetValueText.text = newBetValue.toFixed(config.betDisplayPrecision);
        this.flushBetValueText.text = newBetValue.toFixed(config.betDisplayPrecision);
        this.straightFlushBetValueText.text = newBetValue.toFixed(config.betDisplayPrecision);
        this.straightBetValueText.text = newBetValue.toFixed(config.betDisplayPrecision);
        this.threeOfAKindBetValueText.text = newBetValue.toFixed(config.betDisplayPrecision);
        this.twoPairsFlushBetValueText.text = newBetValue.toFixed(config.betDisplayPrecision);
        this.jacksOrBetterFlushBetValueText.text = newBetValue.toFixed(config.betDisplayPrecision);

        /* tslint:disable */
        this.royalFlushWinValueText.text =
            (newBetValue * VideoPokerCombinationMultiplier.get(VideoPokerCardCombinationName.ROYAL_FLUSH)).toFixed(config.betDisplayPrecision);
        this.straightFlushWinValueText.text =
            (newBetValue * VideoPokerCombinationMultiplier.get(VideoPokerCardCombinationName.STRAIGHT_FLUSH)).toFixed(config.betDisplayPrecision);
        this.fourOfAKindWinValueText.text =
            (newBetValue * VideoPokerCombinationMultiplier.get(VideoPokerCardCombinationName.FOUR_OF_A_KIND)).toFixed(config.betDisplayPrecision);
        this.fullHouseWinValueText.text =
            (newBetValue * VideoPokerCombinationMultiplier.get(VideoPokerCardCombinationName.FULL_HOUSE)).toFixed(config.betDisplayPrecision);
        this.flushWinValueText.text =
            (newBetValue * VideoPokerCombinationMultiplier.get(VideoPokerCardCombinationName.FLUSH)).toFixed(config.betDisplayPrecision);
        this.straightWinValueText.text =
            (newBetValue * VideoPokerCombinationMultiplier.get(VideoPokerCardCombinationName.STRAIGHT)).toFixed(config.betDisplayPrecision);
        this.threeOfAKindWinValueText.text =
            (newBetValue * VideoPokerCombinationMultiplier.get(VideoPokerCardCombinationName.THREE_OF_A_KIND)).toFixed(config.betDisplayPrecision);
        this.twoPairsFlushWinValueText.text =
            (newBetValue * VideoPokerCombinationMultiplier.get(VideoPokerCardCombinationName.TWO_PAIRS)).toFixed(config.betDisplayPrecision);
        this.jacksOrBetterFlushWinValueText.text =
            (newBetValue * VideoPokerCombinationMultiplier.get(VideoPokerCardCombinationName.JACKS_OR_BETTER)).toFixed(config.betDisplayPrecision);

        this.royalFlushMaxWinValueText.text =
            (newBetValue * VideoPokerCombinationMultiplier.get(VideoPokerCardCombinationName.ROYAL_FLUSH) * Math.pow(2, config.maxDoublesForRoyalFlush)).toFixed(config.betDisplayPrecision);
        this.straightFlushMaxWinValueText.text =
            (newBetValue * VideoPokerCombinationMultiplier.get(VideoPokerCardCombinationName.STRAIGHT_FLUSH) * Math.pow(2, config.maxDoublesForStraightFlush)).toFixed(config.betDisplayPrecision);
        this.fourOfAKindMaxWinValueText.text =
            (newBetValue * VideoPokerCombinationMultiplier.get(VideoPokerCardCombinationName.FOUR_OF_A_KIND) * Math.pow(2, config.maxDoublesForFourOfKind)).toFixed(config.betDisplayPrecision);
        this.fullHouseMaxWinValueText.text =
            (newBetValue * VideoPokerCombinationMultiplier.get(VideoPokerCardCombinationName.FULL_HOUSE) * Math.pow(2, config.maxDoublesForOtherCombination)).toFixed(config.betDisplayPrecision);
        this.flushMaxWinValueText.text =
            (newBetValue * VideoPokerCombinationMultiplier.get(VideoPokerCardCombinationName.FLUSH) * Math.pow(2, config.maxDoublesForOtherCombination)).toFixed(config.betDisplayPrecision);
        this.straightMaxWinValueText.text =
            (newBetValue * VideoPokerCombinationMultiplier.get(VideoPokerCardCombinationName.STRAIGHT) * Math.pow(2, config.maxDoublesForOtherCombination)).toFixed(config.betDisplayPrecision);
        this.threeOfAKindMaxWinValueText.text =
            (newBetValue * VideoPokerCombinationMultiplier.get(VideoPokerCardCombinationName.THREE_OF_A_KIND) * Math.pow(2, config.maxDoublesForOtherCombination)).toFixed(config.betDisplayPrecision);
        this.twoPairsFlushMaxWinValueText.text =
            (newBetValue * VideoPokerCombinationMultiplier.get(VideoPokerCardCombinationName.TWO_PAIRS) * Math.pow(2, config.maxDoublesForOtherCombination)).toFixed(config.betDisplayPrecision);
        this.jacksOrBetterFlushMaxWinValueText.text =
            (newBetValue * VideoPokerCombinationMultiplier.get(VideoPokerCardCombinationName.JACKS_OR_BETTER) * Math.pow(2, config.maxDoublesForOtherCombination)).toFixed(config.betDisplayPrecision);
        /* tslint:enable */
    }

    private createAndAddWinningDotAnimation(): void {
        this.winningDotAnimation = new WinningDotAnimation();
        this.winningDotAnimation.position = this.WINNING_DOT_INITIAL_POSITION;
        this.addChild(this.winningDotAnimation);
    }

    private createAndAddBackground(): void {
        const payTable: PIXI.Sprite = PIXI.Sprite.fromImage(data.images.paytable);
        payTable.anchor = PredefinedPIXIAnchorPoint.CENTER;
        this.addChild(payTable);
    }
}



// WEBPACK FOOTER //
// ./src/app/PayTable.ts